import * as React from "react";
import { useCallback, useState, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useSendTelemetryOnMount from "logic/telemetry/useSendTelemetryOnMount";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import { useEnableLatestPacks, useDisableLatestPacks, useGetPacksSummary } from "illuminate/hooks/usePacksAPI";
import { useModalContext, useSelectedRows } from "common/contexts";
import { ConfirmDialog } from "common/components";
import EntityCreateShareFormGroup from "components/permissions/EntityCreateShareFormGroup";
import CurrentUser from "logic/users/CurrentUser";
import PacksList from "./packs-list";
import { buildEnableDisablePayload } from "../../utils";
const CONST_MAP = {
  enable: {
    data: "packs_modal.data_creation",
    title: "packs_modal.title_enable"
  },
  disable: {
    data: "packs_modal.data_loss",
    title: "packs_modal.title_disable"
  }
};
function ConfEnableDisablePacks({ onCancel }) {
  const { t } = useTranslation("enterprise_illuminate");
  const { entity: actionType } = useModalContext();
  const selectedPacks = useSelectedRows();
  const { enablingPacks, enablePacks } = useEnableLatestPacks();
  const { disablingPacks, disablePacks } = useDisableLatestPacks();
  const [shareRequest, setShareRequest] = useState(void 0);
  const sendTelemetry = useSendTelemetry("packs");
  const {
    packsSummary: { cached_version, current_version }
  } = useGetPacksSummary();
  const updatingPacks = useMemo(() => enablingPacks || disablingPacks, [enablingPacks, disablingPacks]);
  useSendTelemetryOnMount(
    sendTelemetry,
    TELEMETRY_EVENT_TYPE.ILLUMINATE[`PACK_${actionType.toUpperCase()}_MODAL_TOGGLED`],
    {
      app_pathname: "illuminate",
      event_details: {
        illuminate_enterprise: true,
        illuminate_version: ""
      }
    },
    [actionType]
  );
  const handleEnablingPacks = useCallback(async () => {
    const packsToEnable = selectedPacks.filter((pack) => !pack.enabled);
    sendTelemetry(TELEMETRY_EVENT_TYPE.ILLUMINATE.PACKS_BULK_UPDATED, {
      app_pathname: "illuminate",
      event_details: {
        illuminate_enterprise: true,
        illuminate_version: cached_version || current_version,
        illuminate_action: actionType,
        illuminate_packs: packsToEnable.map((pack) => ({
          id: pack.pack_id,
          title: pack.title,
          version: pack.version
        }))
      }
    });
    const entity = buildEnableDisablePayload(packsToEnable);
    return enablePacks({ requestObject: { entity, share_request: shareRequest } });
  }, [actionType, cached_version, current_version, enablePacks, selectedPacks, sendTelemetry, shareRequest]);
  const handleDisablingPacks = useCallback(async () => {
    const packsToDisable = selectedPacks.filter((pack) => pack.enabled);
    sendTelemetry(TELEMETRY_EVENT_TYPE.ILLUMINATE.PACKS_BULK_UPDATED, {
      app_pathname: "illuminate",
      event_details: {
        illuminate_enterprise: true,
        illuminate_version: current_version,
        illuminate_action: actionType,
        illuminate_packs: packsToDisable.map((pack) => ({
          id: pack.pack_id,
          title: pack.title,
          version: pack.version
        }))
      }
    });
    const requestObject = buildEnableDisablePayload(packsToDisable);
    return disablePacks({ requestObject });
  }, [actionType, current_version, disablePacks, selectedPacks, sendTelemetry]);
  const onConfirm = useCallback(async () => {
    if (actionType === "enable") await handleEnablingPacks().then(() => CurrentUser.reload());
    if (actionType === "disable") await handleDisablingPacks().then(() => CurrentUser.reload());
    onCancel();
  }, [actionType, handleDisablingPacks, handleEnablingPacks, onCancel]);
  const combinedAction = actionType === "enable" && selectedPacks.filter((pack) => pack.update_available).length > 0 ? t("packs_modal.action_enable_or_update") : t(`packs_modal.action_${actionType}`);
  return /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: true,
      onConfirm,
      confirmDisabled: updatingPacks,
      isLoading: updatingPacks,
      confirmText: t("packs_modal.confirm"),
      onCancel,
      title: t("packs_modal.title", { count: selectedPacks.length, action: t(CONST_MAP[actionType].title) })
    },
    /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "packs_modal.about_to", values: { action: combinedAction }, components: { bold: /* @__PURE__ */ React.createElement("b", null) } })),
    /* @__PURE__ */ React.createElement("p", null, t("packs_modal.data_warning", { data: t(CONST_MAP[actionType].data) })),
    /* @__PURE__ */ React.createElement("p", null, t("packs_modal.continue_question")),
    /* @__PURE__ */ React.createElement(PacksList, null),
    actionType === "enable" && /* @__PURE__ */ React.createElement(
      EntityCreateShareFormGroup,
      {
        entityType: "Illuminate pack",
        description: t("packs_modal.share_description"),
        entityTitle: "",
        onSetEntityShare: setShareRequest
      }
    )
  );
}
export default ConfEnableDisablePacks;
